import { ref, readonly } from 'vue';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/core/auth/auth';

let socketInstance: Socket | null = null;
const isConnected = ref(false);
const activeRooms = new Set<string>();

/**
 * Cleanly resolves the base origin for Socket.IO connection
 * Strips any trailing '/api' from VITE_API_URL if configured.
 */
function resolveSocketBaseUrl(): string {
  const rawApiUrl = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
  if (rawApiUrl) {
    try {
      const parsed = new URL(rawApiUrl, window.location.origin);
      return parsed.origin;
    } catch {
      return rawApiUrl.replace(/\/api\/?$/, '');
    }
  }
  return window.location.origin;
}

/**
 * Initializes and returns the shared singleton Socket.IO client instance.
 */
export function getSocket(): Socket {
  if (socketInstance) {
    return socketInstance;
  }

  const authStore = useAuthStore();
  const baseUrl = resolveSocketBaseUrl();

  socketInstance = io(baseUrl, {
    path: '/socket.io/',
    auth: (cb: (data: object) => void) => {
      const currentToken = authStore.token || localStorage.getItem('token') || '';
      const u = authStore.user as any;
      const currentName = u?.name || u?.username || u?.email || '';
      cb({
        token: currentToken,
        name: currentName,
      });
    },
    transports: ['websocket', 'polling'],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1500,
  });

  socketInstance.on('connect', () => {
    isConnected.value = true;
    console.log(`[Socket.IO] 🟢 Connected to real-time server (${socketInstance?.id})`);

    // Rejoin all active rooms on reconnect
    for (const room of activeRooms) {
      socketInstance?.emit('join:room', room);
    }
  });

  socketInstance.on('disconnect', (reason) => {
    isConnected.value = false;
    console.warn(`[Socket.IO] 🔴 Disconnected from real-time server: ${reason}`);
  });

  socketInstance.on('connect_error', async (error) => {
    isConnected.value = false;
    console.warn(`[Socket.IO] ⚠️ Connection error:`, error.message);
    if (error.message?.includes('Invalid token') || error.message?.includes('Authentication error')) {
      try {
        await authStore.refreshToken();
      } catch (_) {}
    }
  });

  return socketInstance;
}

/**
 * Vue Composable for interacting with the shared Socket.IO instance.
 */
export function useSocket() {
  const socket = getSocket();

  const joinRoom = (room: string) => {
    if (!room) return;
    activeRooms.add(room);
    if (socket.connected) {
      socket.emit('join:room', room);
    }
  };

  const leaveRoom = (room: string) => {
    if (!room) return;
    activeRooms.delete(room);
    if (socket.connected) {
      socket.emit('leave:room', room);
    }
  };

  const on = (event: string, handler: (...args: any[]) => void) => {
    socket.on(event, handler);
  };

  const off = (event: string, handler?: (...args: any[]) => void) => {
    if (handler) {
      socket.off(event, handler);
    } else {
      socket.off(event);
    }
  };

  const emit = (event: string, ...args: any[]) => {
    socket.emit(event, ...args);
  };

  return {
    socket,
    isConnected: readonly(isConnected),
    joinRoom,
    leaveRoom,
    on,
    off,
    emit,
  };
}
